import { createExpressionContext, evaluateExpressionSafe } from "./expression-sandbox.js";
import { EXPRESSION_SOURCE } from "./design-fault-reporting.js";
import { buildItemTypeMap, getDefaultItemValue, isReadOnly } from "./form-helpers.js";
import { isComputationItem } from "./method-types.js";

export const updateVisibility = (form, visibilityFunctions, resetField = null) => {
    // On a read-only form, don't clear formData for hidden items: a dependency
    // drift since submission could flip an answered item to hidden and wipe a
    // value the patient actually submitted.
    const readOnly = isReadOnly(form.status);
    // A form can arrive without formData; hiding an item writes into it, so
    // normalize it up front instead of throwing mid-pass.
    form.formData ??= {};
    // One evaluation context per pass, like every other expression pass:
    // formData is coerced by item type once and shared by all items instead of
    // per item (O(items²) per pass).
    const context = createExpressionContext({
        formData: form.formData,
        dependencies: form.dependencies ?? {},
        identifierModel: form.identifierModel ?? {},
        itemTypes: buildItemTypeMap(form),
    });

    // Resets a hidden/gated item's value where it is stored, mirrored into the
    // shared context so later expressions in this pass see it (coercing
    // null/[] is an identity, so the raw default is already the coerced value).
    // A value already at its default is left alone: besides avoiding a no-op
    // RHF field reset, this keeps a hidden Checkboxes item's formData array
    // referentially stable, so the recompute hook's identity diff doesn't see
    // a fresh [] as a change on every pass. "" still resets to null so
    // expressions see one empty value, and a null Checkboxes still resets to
    // [] so array helpers don't throw on it.
    const resetItemValue = (item) => {
        const defaultValue = getDefaultItemValue(item.itemType);
        const current = form.formData[item.oid];
        const alreadyEmpty = Array.isArray(defaultValue)
            ? Array.isArray(current) && current.length === 0
            : current === defaultValue;
        if (alreadyEmpty) {
            return;
        }
        form.formData[item.oid] = defaultValue;
        context.formData[item.oid] = defaultValue;
        if (resetField) {
            resetField(item.oid);
        }
    };

    for (const itemGroup of form.itemGroups) {
        for (const item of itemGroup.items) {
            item.isVisible = setIsVisible(item, visibilityFunctions, context);

            // A hide-always item keeps its value (isHiddenAlways); Computation
            // values in a gated group are cleared below, once the group's
            // visibility is final for the pass.
            if (!item.isVisible && !isHiddenAlways(item) && !readOnly) {
                resetItemValue(item);
            }
        }

        // Evaluated after the items: a conditioned group may read its own
        // (just-reset) items, and a group without a condition derives its
        // visibility from theirs.
        itemGroup.isVisible = setIsVisible(itemGroup, visibilityFunctions, context);

        // The function gate: a Computation item in a gated group gets no value
        // from the server either, and a value left in formData would leak into
        // the visibility/chained expressions of later groups. Keyed on the
        // group's end-of-pass visibility — clearing on a transient mid-pass
        // gate would wipe values for a group that ends the pass visible, and
        // the recompute hook's un-gate detection only compares end-of-pass
        // states, so it could never restore them.
        if (!readOnly && isGroupGated(itemGroup)) {
            for (const item of itemGroup.items) {
                if (isComputationItem(item)) {
                    resetItemValue(item);
                }
            }
        }
    }
};

// Server-side Viedoc.Objects.ItemDataStatus, delivered inside formData as
// "{oid}__DATASTATUS" / "{oid}__GROUPDATASTATUS". Any status except Hidden
// means the entry was visible; NotSet means the server has no stored status.
const dataStatus = { notSet: 0, hidden: 4 };

const itemStatusKey = (oid) => `${oid}__DATASTATUS`;
const groupStatusKey = (oid) => `${oid}__GROUPDATASTATUS`;

// The literal string "true" is the format's default for "no visibility
// condition", not an expression to evaluate.
export const hasVisibilityCondition = (node) =>
    Boolean(node.visibility?.advancedExpression) &&
    node.visibility.advancedExpression !== "true";

// hideAlways (ConditionDefModel.HideAlways) marks a node hidden by design, not
// by data: its value is kept and its Computation function still runs, like on
// the server. Imputation defaults stay skipped like on any hidden item — the
// server never executes Imputation methods, so there is no value to mirror.
// The flag, not the expression, decides — activity-context exceptions serialize
// a real expression, not the bare "false" sentinel.
export const isHiddenAlways = (node) => Boolean(node?.visibility?.hideAlways);

// The group half of Clinic's ExecuteFunctions gate: no function value is
// produced for an item whose group is hidden by the group's own condition, and
// hide-always groups are exempt. Falsy, not === false — a throwing condition
// leaves isVisible undefined, which rendering also treats as hidden.
export const isGroupGated = (itemGroup) =>
    hasVisibilityCondition(itemGroup) &&
    !itemGroup.isVisible &&
    !isHiddenAlways(itemGroup);

/**
 * Applies the visibility the server persisted at submit time (DATASTATUS keys
 * in formData) to a submitted (read-only) form: the past form must show
 * exactly what the patient saw, and a cross-form dependency edited in Clinic
 * after submission must not change it. Mirrors how Clinic's own form view
 * renders saved forms.
 *
 * Entries without a stored status (NotSet — data saved before statuses
 * existed) fall back to expression evaluation, like Clinic does.
 */
export const applyStoredVisibility = (form) => {
    form.formData ??= {};

    // Compiled lazily: only NotSet entries need their expression evaluated.
    let evaluate = null;
    const evaluateVisibility = (node) => {
        if (!evaluate) {
            const visibilityFunctions = createVisibilityFunctions(form);
            const context = createExpressionContext({
                formData: form.formData,
                dependencies: form.dependencies ?? {},
                identifierModel: form.identifierModel ?? {},
                itemTypes: buildItemTypeMap(form),
            });
            evaluate = (n) => !!visibilityFunctions[n.oid](context, n);
        }
        return evaluate(node);
    };

    for (const itemGroup of form.itemGroups) {
        for (const item of itemGroup.items) {
            const status =
                form.formData[itemStatusKey(item.oid)] ?? dataStatus.notSet;
            item.isVisible =
                status === dataStatus.notSet
                    ? evaluateVisibility(item)
                    : status !== dataStatus.hidden;
        }

        const status =
            form.formData[groupStatusKey(itemGroup.oid)] ?? dataStatus.notSet;
        // A conditioned group's stored status is the only record of what the
        // patient saw (its expression could evaluate differently today), so it
        // wins. A group without a condition has no visibility of its own — its
        // stored status is just a save-time snapshot of "any item visible" and
        // can contradict the items after a design revision (empty group shell,
        // or a visible item inside a hidden group) — so it is always derived
        // from the items resolved above. Clinic's form view splits the same way.
        itemGroup.isVisible =
            !hasVisibilityCondition(itemGroup) || status === dataStatus.notSet
                ? evaluateVisibility(itemGroup)
                : status !== dataStatus.hidden;
    }
};

export const createVisibilityFunctions = (form) => {
    const visibilityFunctions = {};

    for (const itemGroup of form.itemGroups ?? []) {
        visibilityFunctions[itemGroup.oid] = getVisibilityFunction(
            itemGroup,
            EXPRESSION_SOURCE.ITEM_GROUP_VISIBILITY
        );
        for (const item of itemGroup.items ?? []) {
            visibilityFunctions[item.oid] = getVisibilityFunction(
                item,
                EXPRESSION_SOURCE.ITEM_VISIBILITY
            );
        }
    }

    return visibilityFunctions;
};

const setIsVisible = (item, visibilityFunctions, context) =>
    visibilityFunctions[item.oid](context, item);

const getVisibilityFunction = (item, source) => {
    // The flag is checked before the expression, like the server's IsVisible
    // and Clinic's renderer: an activity-context exception serializes an
    // expression that evaluates TRUE outside the skipped activities, and a
    // condition carrying only non-JS contexts (e.g. EditRoles) serializes an
    // EMPTY one — either way the node must not render, since the server
    // stamps its data status Hidden regardless of the expression.
    if (isHiddenAlways(item)) {
        return () => false;
    }

    // A node with no condition falls through to the defaults below —
    // evaluating the "true" sentinel would force an all-hidden group visible
    // as an empty shell.
    if (hasVisibilityCondition(item)) {
        return createVisibilityFunction(item.visibility.advancedExpression, {
            source,
            oid: item.oid,
            // `label`, the field Me's own item and item-group nodes carry.
            // `question` is the Designer sidebar's name for the same thing and
            // does not exist here — using it left the panel row unattributed.
            label: item.label,
        });
    }

    if (item.items) {
        // Read children from the node passed in by updateVisibility, not the
        // one captured here: a form rebuilt immutably (new item objects, same
        // oids) reuses these functions, so the captured group would report
        // stale child visibility. if all items are hidden, group is also hidden.
        return (_context, node) => node.items.some((i) => i.isVisible);
    }

    return () => true;
};

// Code taken from legacy Viedoc Me — sandbox logic extracted to expression-sandbox.js
//
// `origin` names the condition for the Designer preview's diagnostics panel. It
// changes nothing here: a condition that throws still evaluates to undefined,
// which updateVisibility reads as "not visible", exactly as before.
const createVisibilityFunction = (functionJs, origin) => (context) =>
    evaluateExpressionSafe(functionJs, context, undefined, origin);
